import React, { useState, useEffect, useCallback } from 'react';
import { superAdminAPI, leadsAPI, emailLogsAPI, cmsAPI, blogAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Building2, Users, CreditCard, Activity, Plus, Search, Trash2, Power, KeyRound, ChevronDown, ChevronUp, Shield, BarChart3, Boxes, Megaphone, Download, Filter, Mail, CheckCircle, XCircle, Edit, Globe, FileText, HelpCircle, DollarSign, Star, Save, BookOpen, Eye } from 'lucide-react';

const PLAN_COLORS = { starter: '#3b82f6', professional: '#8b5cf6', enterprise: '#f59e0b' };
const PLAN_BADGES = { starter: 'bg-blue-100 text-blue-800', professional: 'bg-violet-100 text-violet-800', enterprise: 'bg-amber-100 text-amber-800' };

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white rounded-lg border border-slate-200 p-5 flex items-start gap-4">
      <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${color}15` }}><Icon size={20} style={{ color }} /></div>
      <div><p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function CreateCompanyModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', admin_name: '', admin_email: '', admin_password: '', subscription_plan: 'starter', trial_expiry_date: '' });
  const [loading, setLoading] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.admin_name || !form.admin_email || !form.admin_password) { toast.error('All fields required'); return; }
    setLoading(true);
    try {
      await superAdminAPI.createCompany(form);
      toast.success('Company created');
      onCreated(); onClose();
      setForm({ name: '', admin_name: '', admin_email: '', admin_password: '', subscription_plan: 'starter', trial_expiry_date: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Plus size={18} /> Create New Company</h2>
        <form onSubmit={submit} className="space-y-3">
          <div><label className="text-xs font-medium text-slate-600">Company Name *</label>
            <input data-testid="create-company-name" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600">Admin Name *</label>
              <input data-testid="create-admin-name" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={form.admin_name} onChange={e => setForm(p => ({ ...p, admin_name: e.target.value }))} /></div>
            <div><label className="text-xs font-medium text-slate-600">Admin Email *</label>
              <input data-testid="create-admin-email" type="email" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={form.admin_email} onChange={e => setForm(p => ({ ...p, admin_email: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600">Admin Password *</label>
              <input data-testid="create-admin-password" type="password" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={form.admin_password} onChange={e => setForm(p => ({ ...p, admin_password: e.target.value }))} /></div>
            <div><label className="text-xs font-medium text-slate-600">Subscription Plan</label>
              <select data-testid="create-subscription-plan" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={form.subscription_plan} onChange={e => setForm(p => ({ ...p, subscription_plan: e.target.value }))}>
                <option value="starter">Starter</option><option value="professional">Professional</option><option value="enterprise">Enterprise</option>
              </select></div>
          </div>
          <div><label className="text-xs font-medium text-slate-600">Trial Expiry Date (optional)</label>
            <input type="date" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" value={form.trial_expiry_date} onChange={e => setForm(p => ({ ...p, trial_expiry_date: e.target.value }))} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50" onClick={onClose}>Cancel</button>
            <button data-testid="create-company-submit" type="submit" disabled={loading} className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50">{loading ? 'Creating...' : 'Create Company'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompanyRow({ comp, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showFeatures, setShowFeatures] = useState(false);
  const [customFeatures, setCustomFeatures] = useState([]);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const ALL_FEATURES = [
    'invoices', 'receipts', 'basic_accounting', 'ledger', 'profit_loss', 'trial_balance',
    'parties', 'gst_reports', 'route_management', 'vehicle_management', 'driver_management',
    'advanced_reports', 'import_engine', 'data_import', 'masters', 'balance_sheet',
    'client_analytics', 'financial_controls', 'multi_branch', 'advanced_analytics',
    'api_integration', 'custom_reports', 'training_center', 'audit_logs', 'ai_assistant'
  ];

  const startEditing = () => {
    setEditForm({
      name: comp.name || '', address: comp.address || '', gstin: comp.gstin || '',
      cin: comp.cin || '', phone: comp.phone || '', email: comp.email || '',
      financial_year_start: comp.financial_year_start || '', financial_year_end: comp.financial_year_end || '',
      trial_expiry_date: comp.trial_expiry_date || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await superAdminAPI.updateCompany(comp.company_id, editForm);
      toast.success('Company details updated');
      setEditing(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingEdit(false);
  };

  const openFeatures = () => {
    const existing = comp.custom_features || [];
    setCustomFeatures(existing.length > 0 ? existing : ALL_FEATURES);
    setShowFeatures(true);
  };

  const toggleFeature = (feat) => {
    setCustomFeatures(prev => prev.includes(feat) ? prev.filter(f => f !== feat) : [...prev, feat]);
  };

  const saveFeatures = async () => {
    setSavingFeatures(true);
    try {
      await superAdminAPI.updateFeatures(comp.company_id, { features: customFeatures });
      toast.success('Features updated'); setShowFeatures(false); onRefresh();
    } catch (e) { toast.error('Failed'); }
    setSavingFeatures(false);
  };

  const toggleStatus = async () => {
    try {
      await superAdminAPI.updateStatus(comp.company_id, { is_active: !comp.is_active });
      toast.success(comp.is_active ? 'Company deactivated' : 'Company activated');
      onRefresh();
    } catch (e) { toast.error('Failed to update status'); }
  };

  const changePlan = async (plan) => {
    try {
      await superAdminAPI.updateSubscription(comp.company_id, { subscription_plan: plan });
      toast.success(`Plan changed to ${plan}`);
      onRefresh();
    } catch (e) { toast.error('Failed'); }
  };

  const resetPw = async () => {
    if (!newPw || newPw.length < 6) { toast.error('Min 6 chars'); return; }
    setResetting(true);
    try {
      await superAdminAPI.resetPassword(comp.company_id, { new_password: newPw });
      toast.success('Password reset'); setNewPw('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setResetting(false);
  };

  const deleteComp = async () => {
    if (!window.confirm(`DELETE "${comp.name}" and ALL its data? This CANNOT be undone.`)) return;
    if (!window.confirm(`FINAL CONFIRMATION: Type OK to permanently delete "${comp.name}". All users, invoices, journal entries will be lost forever.`)) return;
    try {
      await superAdminAPI.deleteCompany(comp.company_id);
      toast.success('Company deleted');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const purgeData = async () => {
    if (!window.confirm(`Purge ALL transactional data (invoices, receipts, journals) for "${comp.name}"? This CANNOT be undone.`)) return;
    if (!window.confirm(`FINAL CONFIRMATION: This will permanently delete all transactions. Users and settings will be kept.`)) return;
    try {
      await superAdminAPI.purgeData(comp.company_id);
      toast.success('Transactional data purged'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const plan = comp.subscription_plan || 'starter';

  return (
    <div data-testid={`company-row-${comp.company_id}`} className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${comp.is_active ? 'bg-emerald-500' : 'bg-red-400'}`} />
          <div>
            <p className="font-semibold text-sm text-slate-900">{comp.name}</p>
            <p className="text-xs text-slate-400">{comp.company_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${PLAN_BADGES[plan] || PLAN_BADGES.starter}`}>{plan}</span>
          <span className="text-xs text-slate-400">{comp.user_count || 0} users</span>
          {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4 bg-slate-50/50">
          {/* Company Details (View/Edit) */}
          {editing ? (
            <div className="bg-white rounded-lg border p-4 space-y-3" data-testid="edit-company-form">
              <h4 className="text-xs font-semibold text-slate-600 uppercase flex items-center gap-2"><Edit size={13} />Edit Company Details</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[['name','Company Name'],['address','Address'],['gstin','GSTIN'],['cin','CIN'],['phone','Phone'],['email','Email'],['financial_year_start','FY Start'],['financial_year_end','FY End'],['trial_expiry_date','Trial Expiry']].map(([k,l]) => (
                  <div key={k}><label className="text-xs text-slate-500">{l}</label>
                    <input type={k.includes('date') || k.includes('year') ? 'date' : 'text'} value={editForm[k] || ''} onChange={e => setEditForm(p => ({...p, [k]: e.target.value}))}
                      className="w-full mt-0.5 px-2 py-1.5 border rounded-lg text-xs" data-testid={`edit-${k}`} /></div>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditing(false)} className="text-xs px-3 py-1.5 border rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                <button onClick={saveEdit} disabled={savingEdit} className="text-xs px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50" data-testid="save-company-details">
                  {savingEdit ? 'Saving...' : 'Save Details'}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><span className="text-slate-400">Created</span><p className="font-medium text-slate-700">{(comp.created_at || '').slice(0, 10)}</p></div>
              <div><span className="text-slate-400">GSTIN</span><p className="font-medium text-slate-700">{comp.gstin || '-'}</p></div>
              <div><span className="text-slate-400">Phone</span><p className="font-medium text-slate-700">{comp.phone || '-'}</p></div>
              <div><span className="text-slate-400">Email</span><p className="font-medium text-slate-700">{comp.email || '-'}</p></div>
              <div><span className="text-slate-400">Address</span><p className="font-medium text-slate-700 truncate max-w-[200px]">{comp.address || '-'}</p></div>
              <div><span className="text-slate-400">CIN</span><p className="font-medium text-slate-700">{comp.cin || '-'}</p></div>
              <div><span className="text-slate-400">Journals</span><p className="font-medium text-slate-700">{comp.journal_count || 0}</p></div>
              <div><span className="text-slate-400">Trial Expiry</span><p className="font-medium text-slate-700">{comp.trial_expiry_date || '-'}</p></div>
            </div>
          )}

          {/* Feature Toggles */}
          {showFeatures && (
            <div className="bg-white rounded-lg border p-4 space-y-3" data-testid="feature-toggles">
              <h4 className="text-xs font-semibold text-slate-600 uppercase flex items-center gap-2"><Shield size={13} />Feature Access Control</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {ALL_FEATURES.map(feat => (
                  <label key={feat} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-1.5 rounded" data-testid={`feature-toggle-${feat}`}>
                    <input type="checkbox" checked={customFeatures.includes(feat)} onChange={() => toggleFeature(feat)} className="w-3.5 h-3.5 rounded" />
                    <span className="text-slate-700 capitalize">{feat.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 justify-between">
                <div className="flex gap-2">
                  <button onClick={() => setCustomFeatures(ALL_FEATURES)} className="text-xs px-2 py-1 border rounded text-slate-600 hover:bg-slate-50">Select All</button>
                  <button onClick={() => setCustomFeatures([])} className="text-xs px-2 py-1 border rounded text-slate-600 hover:bg-slate-50">Clear All</button>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowFeatures(false)} className="text-xs px-3 py-1.5 border rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button onClick={saveFeatures} disabled={savingFeatures} className="text-xs px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50" data-testid="save-features-btn">
                    {savingFeatures ? 'Saving...' : 'Save Features'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Plan Selection */}
          <div className="flex flex-wrap gap-2">
            <label className="text-xs text-slate-500 font-medium self-center mr-1">Plan:</label>
            {['starter', 'professional', 'enterprise'].map(p => (
              <button key={p} onClick={() => changePlan(p)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${plan === p ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>{p}</button>
            ))}
          </div>

          {/* Actions Row */}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={startEditing} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border text-slate-600 border-slate-200 hover:bg-slate-50" data-testid={`edit-company-${comp.company_id}`}>
              <Edit size={13} />Edit Details
            </button>
            <button onClick={openFeatures} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border text-violet-600 border-violet-200 hover:bg-violet-50" data-testid={`manage-features-${comp.company_id}`}>
              <Shield size={13} />Manage Features
            </button>
            <button data-testid={`toggle-status-${comp.company_id}`} onClick={toggleStatus} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${comp.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}>
              <Power size={13} />{comp.is_active ? 'Deactivate' : 'Activate'}
            </button>
            <button onClick={purgeData} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`purge-data-${comp.company_id}`}>
              <Trash2 size={13} />Purge Data
            </button>
            {!comp.is_default && <button data-testid={`delete-${comp.company_id}`} onClick={deleteComp} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={13} />Delete Company</button>}
          </div>
          <div className="flex items-center gap-2">
            <input type="password" placeholder="New admin password" value={newPw} onChange={e => setNewPw(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-xs w-48" />
            <button onClick={resetPw} disabled={resetting} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50">
              <KeyRound size={13} />{resetting ? '...' : 'Reset Password'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700', converted: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-100 text-slate-500' };

function SalesLeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [leadPage, setLeadPage] = useState(1);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadLoading, setLeadLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    setLeadLoading(true);
    try {
      const res = await leadsAPI.list({ status: statusFilter || undefined, search: searchQ || undefined, page: leadPage, limit: 10 });
      setLeads(res.data.leads); setLeadTotal(res.data.total); setStats(res.data.stats || {});
    } catch { toast.error('Failed to load leads'); }
    setLeadLoading(false);
  }, [statusFilter, searchQ, leadPage]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const changeStatus = async (id, status) => {
    try { await leadsAPI.updateStatus(id, { status }); toast.success('Status updated'); loadLeads(); }
    catch { toast.error('Failed'); }
  };

  const exportCSV = async () => {
    try {
      const res = await leadsAPI.exportCSV();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'transsolution_leads.csv'; a.click();
    } catch { toast.error('Export failed'); }
  };

  return (
    <div data-testid="sales-leads-panel" className="bg-white border rounded-lg p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Megaphone size={15} />Sales Leads ({leadTotal})</h3>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 text-xs">
            {[['new', stats.new], ['contacted', stats.contacted], ['converted', stats.converted], ['closed', stats.closed]].map(([s, c]) => (
              <span key={s} className={`px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[s]}`}>{s}: {c || 0}</span>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input placeholder="Search leads..." value={searchQ} onChange={e => { setSearchQ(e.target.value); setLeadPage(1); }}
              className="pl-8 pr-3 py-1.5 border rounded-lg text-xs w-40" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setLeadPage(1); }}
            className="px-3 py-1.5 border rounded-lg text-xs">
            <option value="">All Status</option>
            <option value="new">New</option><option value="contacted">Contacted</option>
            <option value="converted">Converted</option><option value="closed">Closed</option>
          </select>
          <button onClick={exportCSV} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-lg text-slate-600 hover:bg-slate-50">
            <Download size={13} />CSV
          </button>
        </div>
      </div>
      {leadLoading ? (
        <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : leads.length === 0 ? (
        <p className="text-center text-sm text-slate-400 py-6">No leads yet. They'll appear here when visitors submit the demo form.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b bg-slate-50">
              {['Name', 'Company', 'Email', 'Phone', 'City', 'Type', 'Vehicles', 'Status', 'Date', 'Actions'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-slate-500 font-medium">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.lead_id} className="border-b hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{l.name}</td>
                  <td className="px-3 py-2 text-slate-600">{l.company_name || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{l.email}</td>
                  <td className="px-3 py-2 text-slate-600">{l.phone || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{l.city || '-'}</td>
                  <td className="px-3 py-2 text-slate-600 capitalize">{l.business_type || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{l.vehicle_count || '-'}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[l.status] || STATUS_COLORS.new}`}>{l.status}</span></td>
                  <td className="px-3 py-2 text-slate-400">{(l.created_at || '').slice(0, 10)}</td>
                  <td className="px-3 py-2">
                    <select value={l.status} onChange={e => changeStatus(l.lead_id, e.target.value)} className="text-xs border rounded px-1.5 py-1">
                      <option value="new">New</option><option value="contacted">Contacted</option>
                      <option value="converted">Converted</option><option value="closed">Closed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {leadTotal > 10 && (
        <div className="flex justify-center gap-2 mt-4">
          <button disabled={leadPage <= 1} onClick={() => setLeadPage(p => p - 1)} className="px-3 py-1 text-xs border rounded-lg disabled:opacity-40">Prev</button>
          <span className="text-xs text-slate-500 self-center">Page {leadPage}</span>
          <button disabled={leadPage * 10 >= leadTotal} onClick={() => setLeadPage(p => p + 1)} className="px-3 py-1 text-xs border rounded-lg disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function EmailLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logLoading, setLogLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const res = await emailLogsAPI.list({ status: statusFilter || undefined, search: searchQ || undefined, page: logPage, limit: 10 });
      setLogs(res.data.logs); setLogTotal(res.data.total); setStats(res.data.stats || {});
    } catch { toast.error('Failed to load email logs'); }
    setLogLoading(false);
  }, [statusFilter, searchQ, logPage]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <div data-testid="email-logs-panel" className="bg-white border rounded-lg p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Mail size={15} />Email Logs ({logTotal})</h3>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle size={11} />Sent: {stats.sent || 0}</span>
            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><XCircle size={11} />Failed: {stats.failed || 0}</span>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input placeholder="Search emails..." value={searchQ} onChange={e => { setSearchQ(e.target.value); setLogPage(1); }}
              className="pl-8 pr-3 py-1.5 border rounded-lg text-xs w-40" data-testid="email-logs-search" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setLogPage(1); }}
            className="px-3 py-1.5 border rounded-lg text-xs" data-testid="email-logs-filter">
            <option value="">All Status</option>
            <option value="sent">Sent</option><option value="failed">Failed</option>
          </select>
        </div>
      </div>
      {logLoading ? (
        <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : logs.length === 0 ? (
        <p className="text-center text-sm text-slate-400 py-6">No email logs yet. Emails will be logged here when sent.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b bg-slate-50">
              {['Recipient', 'Subject', 'Status', 'Tags', 'Message ID', 'Error', 'Date'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-slate-500 font-medium">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l._id || l.message_id || `email-log-${i}`} className="border-b hover:bg-slate-50" data-testid={`email-log-row-${i}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{l.recipient}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{l.subject}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full ${l.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{l.status}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{(l.tags || []).join(', ') || '-'}</td>
                  <td className="px-3 py-2 text-slate-400 max-w-[150px] truncate font-mono">{l.message_id || '-'}</td>
                  <td className="px-3 py-2 text-red-500 max-w-[200px] truncate">{l.error_message || '-'}</td>
                  <td className="px-3 py-2 text-slate-400">{(l.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {logTotal > 10 && (
        <div className="flex justify-center gap-2 mt-4">
          <button disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)} className="px-3 py-1 text-xs border rounded-lg disabled:opacity-40">Prev</button>
          <span className="text-xs text-slate-500 self-center">Page {logPage}</span>
          <button disabled={logPage * 10 >= logTotal} onClick={() => setLogPage(p => p + 1)} className="px-3 py-1 text-xs border rounded-lg disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function CMSEditorPanel() {
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    cmsAPI.get()
      .then(r => {
        // Stamp every existing CMS array item with a stable React key so
        // add/remove operations don't confuse React into reusing DOM nodes.
        const data = { ...r.data };
        ['stats', 'features', 'pricing', 'faqs', 'social_proof'].forEach(k => {
          if (Array.isArray(data[k])) {
            data[k] = data[k].map((item, idx) =>
              item && typeof item === 'object' && !item._rid
                ? { ...item, _rid: `rid-init-${k}-${idx}` }
                : item
            );
          }
        });
        setCms(data);
      })
      .catch(() => toast.error('Failed to load CMS'))
      .finally(() => setLoading(false));
  }, []);

  const saveSection = async (section) => {
    setSaving(true);
    try {
      // Strip our synthetic _rid keys before sending to the server.
      const payload = Array.isArray(cms[section])
        ? cms[section].map(({ _rid, ...rest }) => rest)
        : cms[section];
      await cmsAPI.updateSection(section, payload);
      toast.success(`${section} section saved`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const updateField = (section, field, value) => {
    setCms(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const updateArrayItem = (section, idx, field, value) => {
    setCms(prev => {
      const arr = [...(prev[section] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [section]: arr };
    });
  };

  // Monotonic in-session nonce so every CMS array item gets a stable React
  // key even if the user hasn't saved yet. Survives add/remove reorder.
  const nextRid = (() => { let n = 0; return () => `rid-${Date.now()}-${++n}`; })();

  const addArrayItem = (section, template) => {
    setCms(prev => ({ ...prev, [section]: [...(prev[section] || []), { ...template, _rid: nextRid() }] }));
  };

  const removeArrayItem = (section, idx) => {
    setCms(prev => ({ ...prev, [section]: (prev[section] || []).filter((_, i) => i !== idx) }));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!cms) return <p className="text-center text-gray-400 py-12">Failed to load CMS content</p>;

  const SECTIONS = [
    { key: 'hero', label: 'Hero', icon: Globe },
    { key: 'stats', label: 'Stats', icon: BarChart3 },
    { key: 'features', label: 'Features', icon: Star },
    { key: 'pricing', label: 'Pricing', icon: DollarSign },
    { key: 'faqs', label: 'FAQs', icon: HelpCircle },
    { key: 'social_proof', label: 'Social Proof', icon: Users },
    { key: 'global_settings', label: 'Global Settings', icon: Shield },
  ];

  return (
    <div data-testid="cms-editor-panel" className="space-y-4">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${activeSection === s.key ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
            data-testid={`cms-tab-${s.key}`}>
            <s.icon size={14} />{s.label}
          </button>
        ))}
      </div>

      {/* HERO EDITOR */}
      {activeSection === 'hero' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-hero-editor">
          <h3 className="text-sm font-semibold text-purple-700">Hero Section</h3>
          {['badge', 'headline', 'highlight_word', 'subheadline', 'cta_primary', 'cta_secondary', 'trust_line'].map(f => (
            <div key={f}><label className="form-label">{f.replace(/_/g, ' ')}</label>
              {f === 'subheadline' ? <textarea className="form-input min-h-[60px]" value={cms.hero?.[f] || ''} onChange={e => updateField('hero', f, e.target.value)} data-testid={`cms-hero-${f}`} />
                : <input className="form-input" value={cms.hero?.[f] || ''} onChange={e => updateField('hero', f, e.target.value)} data-testid={`cms-hero-${f}`} />}
            </div>
          ))}
          <button onClick={() => saveSection('hero')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs" data-testid="save-hero"><Save size={14} />{saving ? 'Saving...' : 'Save Hero'}</button>
        </div>
      )}

      {/* STATS EDITOR */}
      {activeSection === 'stats' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-stats-editor">
          <h3 className="text-sm font-semibold text-purple-700">Stats Cards</h3>
          {(cms.stats || []).map((s, i) => (
            <div key={s._rid || `stats-${i}`} className="flex gap-2 items-end">
              <div className="flex-1"><label className="form-label">Value</label><input className="form-input" value={s.value} onChange={e => updateArrayItem('stats', i, 'value', e.target.value)} /></div>
              <div className="flex-1"><label className="form-label">Label</label><input className="form-input" value={s.label} onChange={e => updateArrayItem('stats', i, 'label', e.target.value)} /></div>
              <button onClick={() => removeArrayItem('stats', i)} className="p-2 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => addArrayItem('stats', { value: '0', label: 'New Stat' })} className="text-xs text-purple-600 hover:text-purple-700 font-medium">+ Add Stat</button>
          <button onClick={() => saveSection('stats')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs"><Save size={14} />{saving ? 'Saving...' : 'Save Stats'}</button>
        </div>
      )}

      {/* FEATURES EDITOR */}
      {activeSection === 'features' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-features-editor">
          <h3 className="text-sm font-semibold text-purple-700">Feature Cards ({(cms.features || []).length})</h3>
          {(cms.features || []).map((f, i) => (
            <div key={f._rid || `feature-${i}`} className="border border-purple-100 dark:border-purple-900/30 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div><label className="form-label">Title</label><input className="form-input text-xs" value={f.title} onChange={e => updateArrayItem('features', i, 'title', e.target.value)} /></div>
                <div className="col-span-2"><label className="form-label">Description</label><input className="form-input text-xs" value={f.desc} onChange={e => updateArrayItem('features', i, 'desc', e.target.value)} /></div>
              </div>
              <div className="flex justify-between items-center">
                <div className="w-32"><label className="form-label">Icon</label>
                  <select className="form-input text-xs" value={f.icon || ''} onChange={e => updateArrayItem('features', i, 'icon', e.target.value)}>
                    {['BookOpen','FileText','IndianRupee','Wallet','Landmark','BarChart3','Users','Calculator','TrendingUp','Shield','Truck','Globe','Star','Zap','Building2'].map(ic => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                </div>
                <button onClick={() => removeArrayItem('features', i)} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"><Trash2 size={12} />Remove</button>
              </div>
            </div>
          ))}
          <button onClick={() => addArrayItem('features', { title: 'New Feature', desc: 'Description', icon: 'Star' })} className="text-xs text-purple-600 hover:text-purple-700 font-medium">+ Add Feature</button>
          <button onClick={() => saveSection('features')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs"><Save size={14} />{saving ? 'Saving...' : 'Save Features'}</button>
        </div>
      )}

      {/* PRICING EDITOR */}
      {activeSection === 'pricing' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-pricing-editor">
          <h3 className="text-sm font-semibold text-purple-700">Pricing Plans</h3>
          {(cms.pricing || []).map((p, i) => (
            <div key={p._rid || `pricing-${i}`} className="border border-purple-100 dark:border-purple-900/30 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div><label className="form-label">Plan Name</label><input className="form-input text-xs" value={p.name} onChange={e => updateArrayItem('pricing', i, 'name', e.target.value)} /></div>
                <div><label className="form-label">Price</label><input className="form-input text-xs" value={p.price} onChange={e => updateArrayItem('pricing', i, 'price', e.target.value)} /></div>
                <div><label className="form-label">Period</label><input className="form-input text-xs" value={p.period} onChange={e => updateArrayItem('pricing', i, 'period', e.target.value)} /></div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={p.popular || false} onChange={e => updateArrayItem('pricing', i, 'popular', e.target.checked)} className="rounded" />Most Popular</label>
                <button onClick={() => removeArrayItem('pricing', i)} className="text-xs text-red-400 hover:text-red-600 ml-auto flex items-center gap-1"><Trash2 size={12} />Remove</button>
              </div>
              <div><label className="form-label">Features (one per line)</label>
                <textarea className="form-input text-xs min-h-[60px]" value={(p.features || []).join('\n')} onChange={e => updateArrayItem('pricing', i, 'features', e.target.value.split('\n'))} />
              </div>
            </div>
          ))}
          <button onClick={() => addArrayItem('pricing', { name: 'New Plan', price: '999', period: 'month', popular: false, features: ['Feature 1'] })} className="text-xs text-purple-600 hover:text-purple-700 font-medium">+ Add Plan</button>
          <button onClick={() => saveSection('pricing')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs"><Save size={14} />{saving ? 'Saving...' : 'Save Pricing'}</button>
        </div>
      )}

      {/* FAQ EDITOR */}
      {activeSection === 'faqs' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-faq-editor">
          <h3 className="text-sm font-semibold text-purple-700">FAQs ({(cms.faqs || []).length})</h3>
          {(cms.faqs || []).map((f, i) => (
            <div key={f._rid || `faq-${i}`} className="flex gap-2 items-start">
              <div className="flex-1 space-y-1">
                <input className="form-input text-xs" placeholder="Question" value={f.question} onChange={e => updateArrayItem('faqs', i, 'question', e.target.value)} />
                <textarea className="form-input text-xs min-h-[40px]" placeholder="Answer" value={f.answer} onChange={e => updateArrayItem('faqs', i, 'answer', e.target.value)} />
              </div>
              <button onClick={() => removeArrayItem('faqs', i)} className="p-2 text-red-400 hover:text-red-600 mt-1"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => addArrayItem('faqs', { question: 'New Question?', answer: 'Answer here.' })} className="text-xs text-purple-600 hover:text-purple-700 font-medium">+ Add FAQ</button>
          <button onClick={() => saveSection('faqs')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs"><Save size={14} />{saving ? 'Saving...' : 'Save FAQs'}</button>
        </div>
      )}

      {/* SOCIAL PROOF EDITOR */}
      {activeSection === 'social_proof' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-social-editor">
          <h3 className="text-sm font-semibold text-purple-700">Social Proof / Testimonials</h3>
          {(cms.social_proof || []).map((s, i) => (
            <div key={s._rid || `social-${i}`} className="flex gap-2 items-start">
              <div className="flex-1 grid grid-cols-3 gap-2">
                <div><label className="form-label">Name</label><input className="form-input text-xs" value={s.name} onChange={e => updateArrayItem('social_proof', i, 'name', e.target.value)} /></div>
                <div><label className="form-label">Role</label><input className="form-input text-xs" value={s.role} onChange={e => updateArrayItem('social_proof', i, 'role', e.target.value)} /></div>
                <div><label className="form-label">Quote</label><input className="form-input text-xs" value={s.quote} onChange={e => updateArrayItem('social_proof', i, 'quote', e.target.value)} /></div>
              </div>
              <button onClick={() => removeArrayItem('social_proof', i)} className="p-2 text-red-400 hover:text-red-600 mt-5"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => addArrayItem('social_proof', { name: 'Client Name', role: 'CEO', quote: 'Great product!' })} className="text-xs text-purple-600 hover:text-purple-700 font-medium">+ Add Testimonial</button>
          <button onClick={() => saveSection('social_proof')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs"><Save size={14} />{saving ? 'Saving...' : 'Save Social Proof'}</button>
        </div>
      )}

      {/* GLOBAL SETTINGS EDITOR */}
      {activeSection === 'global_settings' && (
        <div className="glass-card rounded-xl p-5 space-y-3" data-testid="cms-global-settings">
          <h3 className="text-sm font-semibold text-purple-700">Global Settings (Fonts & Colors)</h3>
          <p className="text-[11px] text-gray-500">These settings apply globally across the landing page and app theme.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Heading Font</label>
              <select className="form-input text-xs" value={cms.global_settings?.heading_font || 'Outfit'} onChange={e => updateField('global_settings', 'heading_font', e.target.value)} data-testid="cms-heading-font">
                <option value="Outfit">Outfit</option>
                <option value="Inter">Inter</option>
                <option value="Poppins">Poppins</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Playfair Display">Playfair Display</option>
                <option value="DM Sans">DM Sans</option>
                <option value="Space Grotesk">Space Grotesk</option>
              </select>
            </div>
            <div>
              <label className="form-label">Body Font</label>
              <select className="form-input text-xs" value={cms.global_settings?.body_font || 'Manrope'} onChange={e => updateField('global_settings', 'body_font', e.target.value)} data-testid="cms-body-font">
                <option value="Manrope">Manrope</option>
                <option value="Inter">Inter</option>
                <option value="DM Sans">DM Sans</option>
                <option value="Nunito">Nunito</option>
                <option value="Lato">Lato</option>
                <option value="Open Sans">Open Sans</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'hero_text_color', label: 'Hero Text Color' },
              { key: 'hero_highlight_color', label: 'Highlight Color' },
              { key: 'primary_color', label: 'Primary Color' },
              { key: 'secondary_color', label: 'Secondary Color' },
            ].map(c => (
              <div key={c.key}>
                <label className="form-label">{c.label}</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={cms.global_settings?.[c.key] || '#9400D3'} onChange={e => updateField('global_settings', c.key, e.target.value)}
                    className="w-8 h-8 rounded border border-purple-200 cursor-pointer" data-testid={`cms-${c.key}`} />
                  <input className="form-input text-xs flex-1 font-mono" value={cms.global_settings?.[c.key] || ''} onChange={e => updateField('global_settings', c.key, e.target.value)} />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => saveSection('global_settings')} disabled={saving} className="btn-primary flex items-center gap-2 text-xs" data-testid="save-global-settings"><Save size={14} />{saving ? 'Saving...' : 'Save Global Settings'}</button>
        </div>
      )}
    </div>
  );
}

function BlogManagementPanel() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', tags: '', cover_image: '', meta_title: '', meta_description: '', status: 'draft' });

  const load = () => {
    setLoading(true);
    blogAPI.listAdmin({ limit: 50 }).then(r => { setPosts(r.data.posts || []); setTotal(r.data.total || 0); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: '', excerpt: '', content: '', tags: '', cover_image: '', meta_title: '', meta_description: '', status: 'draft' }); setEditing(null); };

  const save = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      if (editing) { await blogAPI.update(editing, form); toast.success('Post updated'); }
      else { await blogAPI.create(form); toast.success('Post created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try { await blogAPI.delete(id); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const editPost = async (post) => {
    // Fetch full content
    try {
      const full = await blogAPI.listAdmin({ limit: 50 });
      // Find in admin list - we need to fetch the full post with content
      const res = await blogAPI.getPublic(post.slug).catch(() => null);
      const p = res?.data || post;
      setForm({
        title: p.title || '', excerpt: p.excerpt || '', content: p.content || '',
        tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''),
        cover_image: p.cover_image || '', meta_title: p.meta_title || '',
        meta_description: p.meta_description || '', status: p.status || 'draft',
      });
      setEditing(post.post_id);
    } catch { toast.error('Failed to load post'); }
  };

  return (
    <div className="space-y-5" data-testid="blog-management">
      {/* Editor */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"><BookOpen size={16} /> {editing ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label text-xs">Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="form-input text-sm" placeholder="Blog post title" data-testid="blog-title-input" /></div>
            <div><label className="form-label text-xs">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="form-input text-sm" data-testid="blog-status-select">
                <option value="draft">Draft</option><option value="published">Published</option>
              </select></div>
          </div>
          <div><label className="form-label text-xs">Excerpt (short description for cards)</label>
            <textarea value={form.excerpt} onChange={e => setForm(f => ({...f, excerpt: e.target.value}))} className="form-input text-sm min-h-[60px]" placeholder="Brief description..." data-testid="blog-excerpt-input" /></div>
          <div><label className="form-label text-xs">Content (Markdown supported)</label>
            <textarea value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} className="form-input text-sm min-h-[200px] font-mono" placeholder="## Heading\n\nParagraph text here..." data-testid="blog-content-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label text-xs">Tags (comma separated)</label>
              <input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} className="form-input text-sm" placeholder="GST, Transport, India" data-testid="blog-tags-input" /></div>
            <div><label className="form-label text-xs">Cover Image URL</label>
              <input value={form.cover_image} onChange={e => setForm(f => ({...f, cover_image: e.target.value}))} className="form-input text-sm" placeholder="https://..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label text-xs">SEO Title (max 70 chars)</label>
              <input value={form.meta_title} onChange={e => setForm(f => ({...f, meta_title: e.target.value}))} className="form-input text-sm" placeholder="SEO optimized title" maxLength={70} /></div>
            <div><label className="form-label text-xs">SEO Description (max 160 chars)</label>
              <input value={form.meta_description} onChange={e => setForm(f => ({...f, meta_description: e.target.value}))} className="form-input text-sm" placeholder="Meta description for search engines" maxLength={160} /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary flex items-center gap-2 text-xs" data-testid="blog-save-btn"><Save size={14} /> {editing ? 'Update Post' : 'Create Post'}</button>
            {editing && <button onClick={resetForm} className="btn-secondary text-xs">Cancel Edit</button>}
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-4">All Blog Posts ({total})</h3>
        {loading ? <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div> :
         posts.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No blog posts yet</p> :
          <div className="space-y-2">
            {posts.map(p => (
              <div key={p.post_id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:bg-slate-50" data-testid={`blog-row-${p.post_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-800 truncate">{p.title}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{p.status}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                    <span>by {p.author_name}</span>
                    {p.views > 0 && <span className="flex items-center gap-1"><Eye size={10} />{p.views}</span>}
                    {p.tags?.length > 0 && <span>{p.tags.slice(0, 2).join(', ')}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {p.status === 'published' && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-100 rounded" title="View post"><Eye size={14} className="text-slate-400" /></a>}
                  <button onClick={() => editPost(p)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit"><Edit size={14} className="text-slate-400" /></button>
                  <button onClick={() => deletePost(p.post_id)} className="p-1.5 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14} className="text-red-400" /></button>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}


export default function SuperAdminPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, compRes] = await Promise.all([
        superAdminAPI.dashboard(),
        superAdminAPI.companies({ search, plan: planFilter || undefined, page, limit: 20 })
      ]);
      setDashboard(dashRes.data);
      setCompanies(compRes.data.companies);
      setTotal(compRes.data.total);
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  }, [search, planFilter, page]);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== 'super_admin') {
    return <div className="p-8 text-center"><Shield size={48} className="mx-auto text-slate-300 mb-4" /><h2 className="text-lg font-bold text-slate-700">Access Denied</h2><p className="text-sm text-slate-500 mt-1">Super Admin access required</p></div>;
  }

  const d = dashboard;

  return (
    <div data-testid="super-admin-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-slate-900">Super Admin Control Panel</h1>
          <p className="text-sm text-slate-500">Manage companies, subscriptions, and system access</p></div>
        <button data-testid="create-company-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition-colors">
          <Plus size={16} />Create Company
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/60 dark:bg-[hsl(260,15%,10%)] rounded-lg border border-purple-100 dark:border-purple-900/30 p-1">
        {[
          { key: 'companies', label: 'Companies', icon: Building2 },
          { key: 'cms', label: 'CMS Editor', icon: Globe },
          { key: 'blog', label: 'Blog Posts', icon: BookOpen },
          { key: 'leads', label: 'Sales Leads', icon: Megaphone },
          { key: 'emails', label: 'Email Logs', icon: Mail },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${activeTab === t.key ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-sm' : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'companies' && (
        <>
          {d && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Building2} label="Total Companies" value={d.total_companies} sub={`${d.active_companies} active`} color="#9400D3" />
              <StatCard icon={Users} label="Total Users" value={d.total_users} color="#ED80E9" />
              <StatCard icon={CreditCard} label="Subscriptions" value={`${d.plan_distribution?.enterprise || 0} Ent`} sub={`${d.plan_distribution?.professional || 0} Pro / ${d.plan_distribution?.starter || 0} Starter`} color="#f59e0b" />
              <StatCard icon={Activity} label="System Usage" value={d.system_usage?.total_journal_entries || 0} sub={`${d.system_usage?.total_invoices || 0} inv / ${d.system_usage?.total_receipts || 0} rcpt`} color="#10b981" />
            </div>
          )}

          <div className="bg-white dark:bg-[hsl(260,15%,10%)] border border-purple-100 dark:border-purple-900/30 rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">All Companies ({total})</h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="search-companies" placeholder="Search companies..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                    className="form-input pl-8 w-48 text-xs" />
                </div>
                <select data-testid="filter-plan" value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
                  className="form-input text-xs w-36">
                  <option value="">All Plans</option>
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
            ) : companies.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-8">No companies found</p>
            ) : (
              <div className="space-y-2">
                {companies.map(c => <CompanyRow key={c.company_id} comp={c} onRefresh={load} />)}
              </div>
            )}
          </div>
          <CreateCompanyModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
        </>
      )}

      {activeTab === 'cms' && <CMSEditorPanel />}
      {activeTab === 'blog' && <BlogManagementPanel />}
      {activeTab === 'leads' && <SalesLeadsPanel />}
      {activeTab === 'emails' && <EmailLogsPanel />}
    </div>
  );
}
